import axios from 'axios';

const TOKEN_KEY = 'wms_token';
const USER_KEY = 'wms_user';

// Global axios interceptors: inject Bearer token and handle 401 globally
let onUnauthorized = null;
export const setUnauthorizedHandler = (cb) => {
  onUnauthorized = cb;
};

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // Don't force-logout on the login endpoint itself
      if (!url.includes('/auth/login') && onUnauthorized) {
        onUnauthorized();
      }
    }
    return Promise.reject(error);
  },
);

export const getStoredUser = () => {
  const raw = localStorage.getItem(USER_KEY);
  return raw ? JSON.parse(raw) : null;
};

export const getStoredToken = () => localStorage.getItem(TOKEN_KEY);

export const persistAuth = (token, user) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

export const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

export const hasPermission = (user, ...perms) => {
  if (!user) return false;
  const p = new Set(user.permissions || []);
  return perms.every((x) => p.has(x));
};

export const hasAnyPermission = (user, ...perms) => {
  if (!user) return false;
  const p = new Set(user.permissions || []);
  return perms.some((x) => p.has(x));
};

export const isRole = (user, ...roles) => {
  if (!user) return false;
  return roles.includes(user.role);
};

export const formatApiError = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return err?.message || 'Something went wrong. Please try again.';
};

export const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  supervisor: 'Supervisor',
  operator: 'Operator',
};

export const PERMISSION_GROUPS = [
  {
    label: 'Dashboard',
    items: [{ key: 'dashboard.view', label: 'View dashboard' }],
  },
  {
    label: 'Customer Inventory',
    items: [
      { key: 'customer_inventory.view', label: 'View customer products' },
      { key: 'customer_inventory.manage', label: 'Create / edit / delete customer products' },
    ],
  },
  {
    label: 'Internal Materials',
    items: [
      { key: 'internal_inventory.view', label: 'View internal materials' },
      { key: 'internal_inventory.manage', label: 'Create / edit / delete internal materials' },
    ],
  },
  {
    label: 'Categories & Racks',
    items: [
      { key: 'categories.view', label: 'View categories' },
      { key: 'categories.manage', label: 'Manage categories' },
      { key: 'racks.assign', label: 'Assign racks (scan)' },
    ],
  },
  {
    label: 'Stock Ledger',
    items: [
      { key: 'ledger.view', label: 'View stock ledger' },
      { key: 'ledger.create', label: 'Record inbound / outbound movements' },
    ],
  },
  {
    label: 'Warehouses',
    items: [
      { key: 'warehouses.view', label: 'View warehouses' },
      { key: 'warehouses.manage', label: 'Manage warehouses' },
    ],
  },
  {
    label: 'Orders & Fulfilment',
    items: [
      { key: 'orders.view', label: 'View orders' },
      { key: 'orders.pick', label: 'Pick orders' },
      { key: 'orders.pack', label: 'Pack orders' },
      { key: 'orders.ship', label: 'Ship orders' },
      { key: 'orders.manage', label: 'Create / cancel orders' },
    ],
  },
  {
    label: 'Financial',
    items: [{ key: 'valuation.view', label: 'View cost / valuation' }],
  },
  {
    label: 'Cycle Counts',
    items: [
      { key: 'cycle_count.view', label: 'View cycle counts' },
      { key: 'cycle_count.perform', label: 'Record physical counts' },
      { key: 'cycle_count.approve', label: 'Approve / reject counts' },
      { key: 'cycle_count.generate', label: 'Generate monthly cycles' },
    ],
  },
  {
    label: 'Requesters',
    items: [
      { key: 'requesters.view', label: 'View requesters & pick from dropdown' },
      { key: 'requesters.manage', label: 'Add / edit / suspend requesters' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { key: 'users.manage', label: 'Manage users & permissions' },
      { key: 'audit.view', label: 'View audit trail' },
      { key: 'integrations.manage', label: 'Manage integrations' },
      { key: 'api_keys.manage', label: 'Manage API keys' },
    ],
  },
];
